/**
 * Decision Engine
 *
 * Where possible, inputs to functions this module should be given all they need
 * to make a decision (i.e., they should not reach into flux stores to get entities), and they
 * should return values that express only /which/ entity is preferred, but not
 * necessarily do domain-specific error checking (e.g., returning a null decision
 * if the chosen entity not in the snippet's stores).
 *
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var ApiEvents = require('../api_events');
var Audience = require('../audience');
var bucketing = require('./bucketing');
var deciderFns = require('./decider_fns');
var DecisionError = require('./error').DecisionError;
var di = require('../di');
var enums = require('../constants/enums');
var Flux = require('../flux');
var Group = require('../group');
var Layer = require('../layer');
var logger = require('../utils/logger');
var Policy = require('../utils/policy');
var PluginsStore = di.get('stores/plugins');
var GlobalStore = di.get('stores/global');
var LayerDataStore = di.get('stores/layer_data');

/**
 * Return true if the layer is specified and its policy specifies page ID should be included, false otherwise
 *
 * @param {Layer} [layer]
 * @return {boolean}
 */
exports.isPageIdRelevant = function(layer?:Layer): boolean {
  if (!layer) {
    return false;
  }

  var decider = getDecider(layer.policy);
  if (_.isFunction(decider.includePageIdInDecisionTicket)) {
    return decider.includePageIdInDecisionTicket(layer);
  } else if (decider.includePageIdInDecisionTicket === true) {
    return true;
  }
  return false;
};

/**
 * Creates a DecisionTicket based on the VisitorProfile, audiences
 * @param {Object} decisionTicketData
 * @param {string} decisionTicketData.bucketingId
 * @param {module:stores/visitor~VisitorProfile} decisionTicketData.visitorProfile
 * @param {Audience[]} decisionTicketData.audiences
 * @param {number} decisionTicketData.globalHoldback
 * @param {LayerPreferredVariationMap} decisionTicketData.preferredVariationMap
 * @param {string} [decisionTicketData.pageId]
 * @param {Layer} [decisionTicketData.layer]
 * @return {DecisionTicket}
 * @throws {Error} If decider plugin for specified Layer is not loaded
 */
exports.createTicket = function(decisionTicketData:any): DecisionTicket {
  var ticket = _.pick(decisionTicketData, [
    'bucketingId',
    'globalHoldback',
    'preferredVariationMap',
    'pageId'
  ]);
  _.extend(ticket, {
    audienceIds: evaluateAudienceMemberships(decisionTicketData.visitorProfile, decisionTicketData.audiences),
    // TODO(tyler): could probably be passed in decisionTicketData as well
    activationId: GlobalStore.getActivationId(),
  });
  (__PROFILE__ || __RUM_VERBOSE__) && require('core/env/performance').timeEnd('getDecisionTicket');

  return ticket;
};

/**
 * Decide whether the visitor is in the global holdback?
 * @param {DecisionTicket} decisionTicket
 * @returns {GlobalDecision}
 */
exports.decideGlobal = function(decisionTicket:DecisionTicket): GlobalDecision {
  var isGlobalHoldback:boolean = bucketing.isHoldback(decisionTicket.bucketingId, {
    id: null,
    holdback: decisionTicket.globalHoldback,
  });
  return {
    isGlobalHoldback: isGlobalHoldback
  };
};

/**
 * Decide on a layer from the group
 *
 * @param {Group} group
 * @param {GroupDecisionTicket} groupDecisionTicket
 * @return {GroupDecision}
 * @throws {Error} If an invariant-level error occurs
 */
function decideGroup(group:Group, groupDecisionTicket:GroupDecisionTicket): GroupDecision {
  logger.debug('Decision / Deciding layer for group: ', Group.description(group));

  var preferredLayerId = groupDecisionTicket.preferredLayerId;
  var foundPreferredLayer = !!preferredLayerId;

  var chosenLayerId;
  var reason;

  try {
    chosenLayerId = bucketing.chooseWeightedCandidate(
      groupDecisionTicket.bucketingId, group.id, group.weightDistributions);

    if (!chosenLayerId || chosenLayerId === 'None') {
      reason = 'Group traffic allocation. Visitor maps to a "hole" in the bucket space left by ' +
               'an experiment or campaign that\'s since been removed from the group';
    }
  } catch (e) {
    reason = 'Group traffic allocation. Visitor maps to a point in the bucket space which has ' +
             'never been covered by any experiment or campaign.';
  }

  if (foundPreferredLayer) {
    logger.debug('Decision / Using preferredLayerMap to select layer for group:', Group.description(group));

    if (preferredLayerId !== chosenLayerId) {
      Flux.dispatch(actionTypes.RECORD_STICKY_BUCKETING_FEATURE, {
        feature: 'preferredLayer',
        id: group.id,
      });
      chosenLayerId = preferredLayerId;
    }
  }

  // In the event the visitor maps into either a 'hole' or the unmapped region
  if (reason) {
    return {
      layerId: null,
      reason: reason,
    };
  }

  // Chosen layer not in the group -> either throw or return null GroupDecision
  // TODO(spencer.wilson) Stickiness of group decisions here may be revised pending customer feedback
  if (!_.find(group.weightDistributions, { entityId: chosenLayerId })) {
    // TODO(spencer.wilson) If/when sticky bucketing is a configurable policy,
    // this should be changed to better reflect its meaning
    var preferredStr = foundPreferredLayer ? ' sticky-' : ' non-sticky ';

    var msg = 'Visitor was' + preferredStr + 'bucketed into a campaign ' +
              '(' + chosenLayerId + ') which is not in the group';

    // As this should never occur for a "fresh" (non-sticky) decision, throw
    if (!foundPreferredLayer) {
      throw new DecisionError(msg);
    }

    return {
      layerId: null,
      reason: msg,
    };
  }

  return {
    layerId: chosenLayerId,
  };
}

if (__GROUPS__) {
  exports.decideGroup = decideGroup;
}

/**
 * Decide layer variation by passing decision ticket to CDE.
 *
 * @param {Layer} layer
 * @param {DecisionTicket} decisionTicket
 * @returns {Decision}
 */
exports.decideLayer = function(layer:Layer, decisionTicket:DecisionTicket): Decision {
  logger.debug('Deciding: ', layer, decisionTicket);

  var selectedExperiment;
  var selectedVariation;
  var decider = getDecider(layer.policy);

  var decision:any = {
    layerId: layer.id,
    experimentId: null,
    variationId: null,
    isLayerHoldback: bucketing.isHoldback(decisionTicket.bucketingId, layer),
    isLayerConcluded: !!layer.concluded,
  };

  if (_.isEmpty(layer.experiments)) {
    throw new DecisionError('No experiments in layer.');
  }

  try {
    if (decider.decideLayer) {
      logger.debug('Decision / Using decider\'s custom decideLayer.');
      var layerDecision = decider.decideLayer(layer, decisionTicket);
      selectedExperiment = layerDecision.experiment;
      selectedVariation = layerDecision.variation;
    } else {
      logger.debug('Decision / Using default decideLayer behavior.');
      selectedExperiment = decider.selectExperiment(
        layer, decisionTicket.audienceIds, decisionTicket.bucketingId);
      selectedVariation = deciderFns.selectVariation(
        selectedExperiment, decisionTicket.audienceIds, decisionTicket.bucketingId, decisionTicket.activationId, decisionTicket.preferredVariationMap);
    }
  } catch(error) {
    if (error instanceof DecisionError) {
      // "Expected" error, return the reason no bucket was selected
      decision.reason = error.message;
    } else {
      // "Unexpected" error, return it
      decision.error = error;
    }
  }

  decision.experimentId = selectedExperiment ? selectedExperiment.id : null;
  decision.variationId = selectedVariation ? selectedVariation.id : null;

  if (decision.error) {
    decision.error.name = 'DecisionEngineError';
    ApiEvents.emitError(decision.error);
  }

  if (__INVARIANT__) {
    require('../utils/invariant').isType('Decision', decision);
  }
  return decision;
};

/**
 * If any force variations are present, only those should be activated.
 * @param {Layer} layer
 * @param {string[]} forceVariationIds
 * @returns {Decision}
 */
exports.getDummyLayerDecision = function(layer:Layer, forceVariationIds:string[]): Decision {
  var decision;
  var matchingIds = findMatchingForceVariation(layer, forceVariationIds);
  if (matchingIds) {
    logger.log('Decision / Applying force variation:', matchingIds.variationId, 'to Campaign', Layer.description(layer));
    decision = {
      layerId: layer.id,
      variationId: matchingIds.variationId,
      experimentId: matchingIds.experimentId,
      isLayerHoldback: false,
      isLayerConcluded: false,
      reason: 'force',
    };
  } else {
    logger.log('No variation matches ids:', forceVariationIds, 'in Campaign', Layer.description(layer));
    decision = {
      layerId: layer.id,
      variationId: null,
      experimentId: null,
      isLayerHoldback: false,
      isLayerConcluded: false,
      reason: 'force',
    };
  }

  if (__INVARIANT__) {
    require('../utils/invariant').isType('Decision', decision);
  }

  return decision;
};

/**
 * Temporary helper function to indicate if a visitor should be
 * considered in the cohort for a given layer decision.
 *
 * @param {Decision} decision
 * @returns {boolean}
 */
exports.isInCohort = function(decision:Decision): boolean {
  if (!decision.experimentId || !decision.variationId) {
    // Visitor was not selected for any experiment or the variation selected is paused.
    return false;
  }
  var layer = LayerDataStore.get(decision.layerId);
  // Single experiment holdback is not considered part of the cohort.
  // TODO: remove this nonsense (WEB-911) when we stop abusing the layer holdback principle
  return !(Policy.isSingleExperimentPolicy(layer.policy) && decision.isLayerHoldback);
};

/**
 * Helper function to find the matching force variation
 *
 * @param {Layer} layer
 * @param {string[]} forceVariationIds
 * @returns {object|null}
 */
function findMatchingForceVariation(layer:Layer, forceVariationIds:string[]): any|null {
  for (var x = 0; x < layer.experiments.length; x++) {
    for (var y = 0; y < layer.experiments[x].variations.length; y++) {
      if (forceVariationIds.indexOf(layer.experiments[x].variations[y].id) > -1) {
        return {
          experimentId: layer.experiments[x].id,
          variationId: layer.experiments[x].variations[y].id,
        };
      }
    }
  }
  return null;
}

/**
 * Get decider for the layer's policy
 * @param {string} policy
 * @returns {Object} decider
 * @private
 */
function getDecider(policy:string): any {
  var decider = PluginsStore.getPlugin(enums.PluginTypes.deciders, policy);
  if (_.isEmpty(decider)) {
    throw new Error('No deciders found for policy: ' + policy);
  }
  return decider;
}

/**
 * Returns an array of audience ids the visitorProfile satisfies
 * @param {module:stores/visitor~VisitorProfile} visitorProfile
 * @param {Audience[]} audiences
 * @return {string[]}
 * @private
 */
function evaluateAudienceMemberships(visitorProfile:import('api/core_modules').VisitorProfile, audiences:Audience[]): string[] {
  var matchersMap = PluginsStore.getAllPlugins(enums.PluginTypes.audienceMatchers);
  return _.reduce(audiences, function(memo, audience) {
    if (Audience.isInAudience(visitorProfile, audience, matchersMap)) {
      memo.push(audience.id);
    }
    return memo;
  }, []);
}

